/**
 * Project Asset Checkpoint Utilities
 * 
 * Utility functions for managing grace periods and status calculations
 * for project asset checkpoints.
 */

export type Frequency = 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';

/**
 * Get maximum allowed grace period based on frequency
 * Grace period should not exceed the frequency duration
 * @param frequency - The checkpoint frequency
 * @returns Maximum grace period in days
 */
export const getMaxBufferTime = (frequency: Frequency): number => {
  const maxBufferDays: Record<Frequency, number> = {
    'Daily': 1,
    'Weekly': 7,
    'Fortnightly': 14,
    'Monthly': 30,
    'Quarterly': 90,
    'Half-Yearly': 180,
    'Yearly': 365
  };
  
  return maxBufferDays[frequency] || 1;
};

/**
 * Get default buffer time (grace period) for a given frequency
 */
export const getDefaultBufferTime = (frequency: Frequency): number => {
  const frequencyDefaults: Record<Frequency, number> = {
    'Daily': 1,
    'Weekly': 1,
    'Fortnightly': 2,
    'Monthly': 3,
    'Quarterly': 7,
    'Half-Yearly': 14,
    'Yearly': 30
  };
  return frequencyDefaults[frequency] || 1;
};

/**
 * Get buffer time options for a given frequency
 * Grace period options are limited to not exceed the frequency duration
 */
export const getBufferTimeOptions = (frequency: Frequency): Array<{ value: string; label: string }> => {
  const maxBuffer = getMaxBufferTime(frequency);
  const options = [];
  
  for (let i = 0; i <= maxBuffer; i++) {
    options.push({
      value: i.toString(),
      label: i === 0 ? 'No grace period' : i === 1 ? '1 day' : `${i} days`
    });
  }
  
  return options;
};

/**
 * Calculate overdue date based on due date and buffer time
 */
export const calculateOverdueDate = (dueDate: Date, bufferTimeDays: number = 0): Date => {
  const overdueDate = new Date(dueDate);
  overdueDate.setDate(overdueDate.getDate() + bufferTimeDays);
  return overdueDate;
};

/**
 * Check if a checkpoint is overdue
 */
export const isCheckpointOverdue = (
  dueDate: Date,
  bufferTimeDays: number = 0,
  currentDate: Date = new Date()
): boolean => {
  const overdueDate = calculateOverdueDate(dueDate, bufferTimeDays);
  return currentDate > overdueDate;
};

/**
 * Get checkpoint status based on due date, buffer time, and completion status
 */
export const getCheckpointStatus = (
  dueDate: Date,
  bufferTimeDays: number = 0,
  isCompleted: boolean = false,
  currentDate: Date = new Date()
): 'completed' | 'due' | 'overdue' => {
  if (isCompleted) {
    return 'completed';
  }
  
  const overdueDate = calculateOverdueDate(dueDate, bufferTimeDays);
  if (currentDate > overdueDate) {
    return 'overdue';
  }
  
  return 'due';
};

/**
 * Format buffer time for display
 */
export const formatBufferTime = (bufferTimeDays: number): string => {
  if (bufferTimeDays === 0) {
    return 'No grace period';
  } else if (bufferTimeDays === 1) {
    return '1 day';
  } else {
    return `${bufferTimeDays} days`;
  }
};

/**
 * Get frequency color classes for UI
 */
export const getFrequencyColor = (frequency: string) => {
  const colors = {
    Daily: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
    Weekly: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
    Fortnightly: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
    Monthly: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
    Quarterly: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
    'Half-Yearly': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
    Yearly: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-500' },
  };
  
  return colors[frequency as keyof typeof colors] || 
    { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-500' };
};
